import { FC, useEffect, useState } from 'react';
import { styled, Tooltip } from '@mui/material';
import { Check, Save, CloudDownload } from '@mui/icons-material';
import { Row } from 'components/Layout';
import Button from 'components/Button';

interface DraftButtonsProps {
  onQuickSave: () => Promise<void>;
  isQuickSaving?: boolean;
  disabled?: boolean;
  onDownloadSample?: () => void;
  isDownloading?: boolean;
  allowDownload?: boolean;
  showDownloadButton?: boolean;
}

const FloatingButtonContainer = styled('div')(({ theme }) => ({
  position: 'sticky',
  bottom: theme.spacing(5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
}));

const FloatingButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  pointerEvents: 'auto',
  bottom: '-2px',
  borderRadius: '10rem',
  width: '3rem',
  height: '3rem',
  minWidth: 'auto',
  padding: '10px',
  backgroundColor: theme.palette.primary.main,

  '& svg': {
    color: 'white',
    fontSize: '24px',
  },

  '&:disabled svg': {
    color: 'inherit',
  },
}));

const DraftButtons: FC<DraftButtonsProps> = ({
  onQuickSave,
  isQuickSaving = false,
  disabled = false,
  onDownloadSample,
  isDownloading = false,
  allowDownload = false,
  showDownloadButton = false,
}) => {
  const [recentlyQuickSaved, setRecentlyQuickSaved] = useState(false);

  const onButtonClick = async () => {
    await onQuickSave();
    setRecentlyQuickSaved(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (recentlyQuickSaved) {
        setRecentlyQuickSaved(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [recentlyQuickSaved]);

  if (disabled) return null;

  return (
    <FloatingButtonContainer>
      <Row centered justify="center" spacing="xs">
        <FloatingButton
          variant="contained"
          color="primary"
          onClick={onButtonClick}
          disabled={recentlyQuickSaved || isQuickSaving}
          loading={isQuickSaving}
          iconOnly
        >
          {recentlyQuickSaved ? <Check /> : <Save />}
        </FloatingButton>
        {showDownloadButton && onDownloadSample && (
          <Tooltip
            title="You have to save your report before you can download a sample."
            disableHoverListener={allowDownload}
          >
            <div>
              <FloatingButton
                variant="contained"
                color="primary"
                onClick={onDownloadSample}
                disabled={isDownloading || !allowDownload}
                iconOnly
                loading={isDownloading}
              >
                <CloudDownload />
              </FloatingButton>
            </div>
          </Tooltip>
        )}
      </Row>
    </FloatingButtonContainer>
  );
};

export default DraftButtons;
